import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner, Card, Row, Col } from 'react-bootstrap';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import { submitSolution } from '../services/submissionService';
import { useTracksForSubmit } from '../hooks/useTracks';
import { Plus, Trash } from 'react-bootstrap-icons';
import FileUploader from '../components/FileUploader';
import './SubmitPage.scss';
import {SimpleSection} from '../components/Section';
import config from '../config';

const SubmitPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submissionDeadline, setSubmissionDeadline] = useState(null);
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);
  const navigate = useNavigate();
  
  // Get dynamic tracks
  const { options: trackOptions, loading: tracksLoading } = useTracksForSubmit();
  
  const [formData, setFormData] = useState({
    title: '',
    track: '' // Start empty, will be set when tracks load
  });
  
  // Authors state - array of author objects
  const [authors, setAuthors] = useState([
    { name: '', affiliation: '' }
  ]);
  
  const [files, setFiles] = useState([]);
  
  useEffect(() => {
    checkAuthStatus();
    checkSubmissionDeadline();
  }, []);
  
  // Update default track when options load
  useEffect(() => {
    if (trackOptions.length > 0 && !formData.track) {
      setFormData(prev => ({
        ...prev,
        track: trackOptions[0].value // This will now be the actual track name like "Smart Charging"
      }));
    }
  }, [trackOptions, formData.track]);

  async function checkSubmissionDeadline() {
    try {
      const response = await fetch(`${config.api.url}admin/challenge-config?format=json`);
      if (response.ok) {
        const data = await response.json();
        if (data.submissionDeadline) {
          const deadline = new Date(data.submissionDeadline);
          const now = new Date();
          setSubmissionDeadline(deadline);
          setIsDeadlinePassed(now > deadline);
        }
      }
    } catch (err) {
      // Continue without deadline if fetch fails
    }
  }
  
  async function checkAuthStatus() {
    try {
      const user = await Auth.currentAuthenticatedUser();
      setIsAuthenticated(true);
      
      // Populate first author name from user's first and last name
      const firstName = user.attributes?.given_name || '';
      const lastName = user.attributes?.family_name || '';
      const fullName = [firstName, lastName].filter(Boolean).join(' ');
      
      if (fullName) {
        setAuthors(prev => [
          { ...prev[0], name: fullName },
          ...prev.slice(1)
        ]);
      }
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleAuthorChange = (index, field, value) => {
    const newAuthors = [...authors];
    newAuthors[index][field] = value;
    setAuthors(newAuthors);
  };

  const addAuthor = () => {
    setAuthors([...authors, { name: '', affiliation: '' }]);
  };

  const removeAuthor = (index) => {
    if (authors.length > 1) {
      const newAuthors = authors.filter((_, i) => i !== index);
      setAuthors(newAuthors);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.title.trim()) {
      setError('Please enter a title for your submission.');
      return;
    }
    
    // Validate authors
    const validAuthors = authors.filter(author => author.name.trim());
    if (validAuthors.length === 0) {
      setError('Please provide at least one author name.');
      return;
    }
    
    if (files.length === 0) {
      setError('Please upload at least one file for your submission.');
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      
      // Submit solution with authors instead of description
      const result = await submitSolution(
        formData.title,
        validAuthors, // Pass authors array instead of description
        formData.track,
        files
      );
      
      // Show success message with details
      let successMessage = 'Your solution has been submitted successfully!';
      if (result.uploadErrors && result.uploadErrors.length > 0) {
        successMessage += ` Note: ${result.uploadErrors.length} file(s) failed to upload.`;
      }
      setSuccess(successMessage);
      
      // Reset form
      setFormData({
        title: '',
        track: trackOptions.length > 0 ? trackOptions[0].value : '' // Use actual track name
      });
      setAuthors([{ name: '', affiliation: '' }]);
      setFiles([]);
      
      // Redirect to submissions page after a delay
      setTimeout(() => {
        navigate('/user/submissions');
      }, 3000);
      
    } catch (err) {
      setError(`Failed to submit solution: ${err.message}`);
      console.error('Error submitting solution:', err);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <SimpleSection>
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </SimpleSection>
    );
  }
  
  if (!isAuthenticated) {
    return (
      <SimpleSection>
        <Alert variant="warning">
          <Alert.Heading>Authentication Required</Alert.Heading>
          <p>
            You need to be logged in to submit a solution. Please go to the{' '}
            <Alert.Link onClick={() => navigate('/user')}>
              user page
            </Alert.Link>{' '}
            to sign in or register.
          </p>
        </Alert>
      </SimpleSection>
    );
  }
  
  return (<>
    <SimpleSection>
      <h2 className="mb-4">Submit Your Solution</h2>
      <p>Solutions for different tracks must be submitted separately. You can
      submit as many time as you want, but only the last version will be graded.</p>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      {/* Submission Deadline Warning */}
      {isDeadlinePassed && (
        <Alert variant="danger">
          <Alert.Heading>Submission Deadline Passed</Alert.Heading>
          <p>
            The submission deadline was {submissionDeadline.toLocaleString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              timeZoneName: 'short'
            })}. 
            New submissions are no longer accepted.
          </p>
        </Alert>
      )}
      
      {submissionDeadline && !isDeadlinePassed && (
        <Alert variant="warning">
          <strong>Submission Deadline:</strong> {submissionDeadline.toLocaleString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              timeZoneName: 'short'
            })}
          <br />
          <small>Make sure to submit your solution before this deadline.</small>
        </Alert>
      )}
      
    </SimpleSection>
    {!isDeadlinePassed && <SimpleSection>
      <Card className="submission-form">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter a title for your submission"
                disabled={submitting}
                required
              />
            </Form.Group>
            
            {/* Authors Section */}
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">Authors *</Form.Label>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={addAuthor}
                  disabled={submitting}
                >
                  <Plus size={16} className="me-1" />
                  Add Author
                </Button>
              </div>
              
              {authors.map((author, index) => (
                <Card key={index} className="mb-2 border-light">
                  <Card.Body className="py-2">
                    <Row>
                      <Col md={5}>
                        <Form.Control
                          type="text"
                          placeholder="Author name"
                          value={author.name}
                          onChange={(e) => handleAuthorChange(index, 'name', e.target.value)}
                          disabled={submitting}
                          required={index === 0} // First author is required
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Control
                          type="text"
                          placeholder="Affiliation (optional)"
                          value={author.affiliation}
                          onChange={(e) => handleAuthorChange(index, 'affiliation', e.target.value)}
                          disabled={submitting}
                        />
                      </Col>
                      <Col md={1} className="d-flex align-items-center">
                        {authors.length > 1 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeAuthor(index)}
                            disabled={submitting}
                            title="Remove author"
                          >
                            <Trash size={14} />
                          </Button>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
              
              <Form.Text className="text-muted">
                Add all authors who contributed to this submission. At least one author name is required.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Track</Form.Label>
              <Form.Select
                name="track"
                value={formData.track}
                onChange={handleInputChange}
                disabled={submitting || tracksLoading}
              >
                {tracksLoading ? (
                  <option>Loading tracks...</option>
                ) : (
                  trackOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                )}
              </Form.Select>
              {tracksLoading && (
                <Form.Text className="text-muted">
                  <Spinner size="sm" className="me-1" />
                  Loading track configuration...
                </Form.Text>
              )}
            </Form.Group>
            
            <Form.Group className="mb-4">
              <Form.Label>Files</Form.Label>
              <FileUploader
                files={files}
                setFiles={setFiles}
                maxFiles={10}
                maxSize={50 * 1024 * 1024} // 50MB
              />
            </Form.Group>
            
            <div className="d-grid">
              <Button
                variant="primary"
                type="submit"
                disabled={submitting || tracksLoading || isDeadlinePassed}
              >
                {submitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Submitting...
                  </>
                ) : (
                  'Submit Solution'
                )}
              </Button>
              {(tracksLoading || isDeadlinePassed) && (
                <small className="text-muted mt-2">
                  {tracksLoading && 'Loading track configuration...'}
                  {isDeadlinePassed && 'Submissions are no longer accepted due to deadline.'}
                </small>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>
    </SimpleSection>}
  </>);
};

export default SubmitPage;
